import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import ContentLibraryKpiGrid from "./ContentLibraryKpiGrid";
import {
  contentAnalyticsColumns,
  contentAnalyticsFiltersToApi,
  getContentAnalyticsHref,
  selectContentAnalyticsPage,
} from "./tableParams/contentAnalyticsColumns";
import { fetchMediaViewsExport } from "../../../api/legacy/ContentAPI";
import StatsAPI from "../../../api/StatsAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import { ANALYST } from "../../../constants/roles";
import useAbility from "../../../hooks/useAbility";
import useDataTableQuery from "../../../hooks/useDataTableQuery";
import useDataTableServerState from "../../../hooks/useDataTableServerState";
import usePeriodFilter from "../../../hooks/usePeriodFilter";
import { navigatorLanguage } from "../../../i18n";
import { getLanguageUuid } from "../../../utils/data/Content";
import PageTitle from "../../common/PageTitle";
import DataTable from "../../dataTable/DataTable";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import InfoPanel from "../../utils/InfoPanel";
import AnalyticsPage from "../AnalyticsPage";
import AnalyticsPeriodPicker from "../charts/AnalyticsPeriodPicker";
import ExportCsvButton from "../charts/ExportCsvButton";
import fetchLegacyExport from "../charts/fetchLegacyExport";
import ContentViewsTimelineCard from "../dashboardAnalytics/ContentViewsTimelineCard";

import type { JSX } from "react";

import type { ExportType } from "./ContentAnalyticsAdmin.types";
import type { AppState } from "../../../reducers";

const infoPanelLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000101763-analytics-content-consumption-report",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000101763-rapports-d-analyse-consommation-des-contenus",
};

function ContentAnalyticsAdmin(): JSX.Element {
  const { hasRole } = useAbility();
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");

  // Conservés uniquement pour l'export CSV via l'API legacy
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );

  const { period, setPeriod } = usePeriodFilter();

  const { language_uuid: languageUuid } = getLanguageUuid(
    languages,
    navigatorLanguage,
  );

  const columnList = useMemo(
    () => contentAnalyticsColumns({ t, projectSlug }),
    [t, projectSlug],
  );

  const serverState = useDataTableServerState({
    columns: columnList,
    defaultSorting: { id: "date", desc: true },
    resetPageOn: [period.startDate.valueOf(), period.endDate.valueOf()],
  });

  const sumMediaViewsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getSumMediaViews,
    params: [
      {
        projectSlug,
        startDate: period.startDate,
        endDate: period.endDate,
        page: serverState.page,
        perPage: serverState.perPage,
        order: serverState.order,
        search: serverState.search === "" ? null : serverState.search,
        filter: contentAnalyticsFiltersToApi(serverState.filters),
      },
    ],
  });

  const contentTable = useDataTableQuery(
    sumMediaViewsQuery,
    selectContentAnalyticsPage,
  );

  const canExport = !contentTable.isLoading && contentTable.data.length > 0;
  const showView = !hasRole([ANALYST]);
  const breadcrumbs = [{ name: t("editor-menu:analytics") }];

  const fetchCsv = async (type: ExportType): Promise<string> =>
    fetchLegacyExport(period.startDate, (periodStart, onSuccess, onError) => {
      fetchMediaViewsExport(
        periodStart.format(API_DATE_FORMAT),
        period.endDate.format(API_DATE_FORMAT),
        languageUuid,
        token,
        projectSlug,
        type,
        onSuccess,
        onError,
        t,
      );
    });

  const exportOptionList = [
    {
      label: t("sumViewsExport"),
      fetchCsv: async () => fetchCsv("sum"),
      fileName: "sum_media_views.csv",
    },
    {
      label: t("fullViewsExport"),
      fetchCsv: async () => fetchCsv("all"),
      fileName: "all_media_views.csv",
    },
  ];

  return (
    <AnalyticsPage
      actions={<AnalyticsPeriodPicker onChange={setPeriod} period={period} />}
      breadcrumbList={breadcrumbs}
      heading={
        <PageTitle>
          {t("editor-menu:content_analytics")}
          <InfoPanel links={infoPanelLinks} />
        </PageTitle>
      }
    >
      <ContentLibraryKpiGrid />
      {showView ? <ContentViewsTimelineCard period={period} /> : null}
      <Card className="gap-4">
        <CardHeader>
          <CardTitle>{t("viewContentsTitle")}</CardTitle>
          <CardAction className="flex items-center gap-2">
            <ExportCsvButton disabled={!canExport} options={exportOptionList} />
          </CardAction>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columnList}
            data={contentTable.data}
            emptyText={t("noDatas")}
            getRowHref={(row) => getContentAnalyticsHref(row, projectSlug)}
            isError={contentTable.isError}
            isLoading={contentTable.isLoading}
            onRetry={contentTable.onRetry}
            rowCount={contentTable.rowCount}
            serverState={serverState}
          />
        </CardContent>
      </Card>
    </AnalyticsPage>
  );
}

export default ContentAnalyticsAdmin;
